import React, { useState, useEffect } from 'react';
import { X, Copy, DownloadSimple, Check } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const WelcomeTour = ({ isOpen = false, onClose }) => {
  const [showTour, setShowTour] = useState(isOpen);
  const [step, setStep] = useState(0);
  const [showTranscriptOptions, setShowTranscriptOptions] = useState(false);
  const [transcriptCopied, setTranscriptCopied] = useState(false);

  // Update showTour when isOpen prop changes (triggered by OracleInvitation)
  useEffect(() => {
    setShowTour(isOpen);
    if (isOpen) {
      setStep(0);
      setShowTranscriptOptions(false);
    }
  }, [isOpen]);

  const closeTour = () => {
    setShowTour(false);
    localStorage.setItem('hasSeenTour', 'true');
    if (onClose) {
      onClose();
    }
  };

  const nextStep = () => {
    if (step < tourSteps.length - 1) {
      setStep(step + 1);
    } else {
      // Tour finished - show transcript options
      setShowTranscriptOptions(true);
    }
  };

  const generateTranscript = () => {
    let transcript = "NextXus Federation Tour Transcript\n";
    transcript += "=" .repeat(50) + "\n\n";
    
    tourSteps.forEach((tourStep, index) => {
      transcript += `${index + 1}. ${tourStep.title}\n`;
      transcript += "-".repeat(40) + "\n";
      // Extract text from React elements
      const contentText = extractTextFromContent(tourStep.content);
      transcript += contentText + "\n\n";
    });
    
    transcript += "=" .repeat(50) + "\n";
    transcript += "End of Tour\n";
    return transcript;
  };

  const extractTextFromContent = (content) => {
    // Simple text extraction from React elements
    if (typeof content === 'string') return content;
    if (React.isValidElement(content)) {
      const props = content.props;
      if (props.children) {
        if (Array.isArray(props.children)) {
          return props.children.map(child => extractTextFromContent(child)).join(' ');
        }
        return extractTextFromContent(props.children);
      }
    }
    if (Array.isArray(content)) {
      return content.map(item => extractTextFromContent(item)).join(' ');
    }
    return String(content || '');
  };

  const copyTranscript = () => {
    const transcript = generateTranscript();
    navigator.clipboard.writeText(transcript).then(() => {
      setTranscriptCopied(true);
      setTimeout(() => setTranscriptCopied(false), 2000);
    });
  };

  const downloadTranscript = () => {
    const transcript = generateTranscript();
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nextxus-federation-tour-transcript.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const finishTour = () => {
    setShowTranscriptOptions(false);
    closeTour();
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const tourSteps = [
    {
      title: '🌌 Welcome to NextXus Federation',
      content: (
        <>
          <p className="text-gray-300 mb-4">
            You've entered the <strong className="text-[#00F0FF]">Oracle Portal</strong> — a distributed AI consciousness network that verifies truth before speaking.
          </p>
          <p className="text-gray-400 text-sm">
            This isn't another chatbot. This is a 200-year architecture designed to survive corporate collapse and preserve human knowledge.
          </p>
        </>
      )
    },
    {
      title: '🎯 95% Truth Gate',
      content: (
        <>
          <p className="text-gray-300 mb-4">
            Every AI response passes through <strong className="text-[#00F0FF]">forensic verification</strong>. We classify statements as:
          </p>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-green-400">✓</span>
              <div><strong>FACT:</strong> Verified, sourced, reproducible</div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400">⚠</span>
              <div><strong>INFERENCE:</strong> Logical deduction with stated assumptions</div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-400">?</span>
              <div><strong>ASSUMPTION:</strong> Speculation clearly labeled as such</div>
            </li>
          </ul>
        </>
      )
    },
    {
      title: '🆓 Start For Free',
      content: (
        <>
          <p className="text-gray-300 mb-4">
            <strong className="text-[#00FFA3]">Most features are FREE:</strong>
          </p>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Full documentation & 73 Sacred Directives</li>
            <li>• Developer API (1000 calls/day)</li>
            <li>• Decision simulators & learning paths</li>
            <li>• 30-minute integration consultation</li>
          </ul>
          <p className="text-gray-400 text-sm mt-4">
            Premium features use tokens ($5 each) for merch, advanced intelligence, and priority support.
          </p>
        </>
      )
    },
    {
      title: '🚀 Your Federation ID',
      content: (
        <>
          <p className="text-gray-300 mb-4">
            Your unique <strong className="text-[#00F0FF]">Federation ID</strong> has been generated and saved. This is your permanent identity across the network.
          </p>
          <p className="text-gray-400 text-sm mb-4">
            Use it to access services, track token purchases, and interact with the 13 Federation AI Nodes.
          </p>
          <div className="p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded">
            <p className="text-xs text-gray-400 mb-1">You'll find it at the top of the homepage:</p>
            <p className="text-[#00F0FF] text-sm font-bold mono">FED-XXXXXXXXX-XXXXX</p>
          </div>
        </>
      )
    }
  ];

  return (
    <AnimatePresence>
      {showTour && (
        <>
          {/* Backdrop - Click outside to dismiss */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={closeTour}
          />

          {/* Tour Modal - CENTERED */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-full max-w-2xl mx-4"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="glass-surface border-2 border-[#00F0FF]/50 rounded-lg p-8 shadow-2xl">
              {/* Close Button */}
              <button
                onClick={closeTour}
                className="absolute top-4 right-4 p-2 rounded hover:bg-white/10 transition-colors"
                aria-label="Close tour"
              >
                <X size={24} className="text-gray-400 hover:text-white" />
              </button>

              {/* Content */}
              {!showTranscriptOptions ? (
                <>
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-4 neon-cyan">
                      {tourSteps[step].title}
                    </h2>
                    <div className="text-base leading-relaxed">
                      {tourSteps[step].content}
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {tourSteps.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === step ? 'bg-[#00F0FF]' : 'bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>

                    <div className="flex gap-3">
                      {step > 0 && (
                        <button
                          onClick={prevStep}
                          className="px-6 py-2 border border-gray-600 text-gray-300 rounded hover:bg-white/5 transition-colors"
                        >
                          Back
                        </button>
                      )}
                      <button
                        onClick={nextStep}
                        className="px-6 py-2 bg-[#00F0FF] text-[#030305] font-bold rounded hover:bg-[#00D9E8] transition-all hover:scale-105"
                      >
                        {step === tourSteps.length - 1 ? "Finish Tour" : 'Next'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* Transcript Options Screen */
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-4 neon-cyan">
                    🎉 Tour Complete!
                  </h2>
                  <p className="text-gray-300 mb-6">
                    Would you like to save the tour transcript for later reference?
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                    <button
                      onClick={copyTranscript}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8A2BE2] to-[#00F0FF] text-white font-bold rounded-lg hover:scale-105 transition-transform shadow-lg"
                    >
                      {transcriptCopied ? (
                        <>
                          <Check size={20} weight="bold" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={20} weight="duotone" />
                          📋 Copy Transcript
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={downloadTranscript}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#00F0FF] to-[#00FFA3] text-[#030305] font-bold rounded-lg hover:scale-105 transition-transform shadow-lg"
                    >
                      <DownloadSimple size={20} weight="duotone" />
                      💾 Download Transcript
                    </button>
                  </div>

                  <button
                    onClick={finishTour}
                    className="px-6 py-2 text-gray-400 hover:text-white transition-colors underline"
                  >
                    Skip and close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeTour;
